import { useEffect, useState, useCallback } from "react";
import AppShell from "@/components/app/AppShell";
import { api, formatErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge, PriorityBadge, statusStripClass, OverdueBadge, OutageBadge } from "@/components/app/Badges";
import ComplaintDetailDialog from "@/components/app/ComplaintDetailDialog";
import RaiseComplaintDialog from "@/components/app/RaiseComplaintDialog";
import OutageBanner from "@/components/app/OutageBanner";
import { toast } from "sonner";
import { Plus, Search, Trash2, Users, Wrench, ShieldCheck, AlertTriangle, Download, KeyRound, Copy, AlertOctagon, Router, Zap } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, AreaChart, Area, CartesianGrid } from "recharts";

const CHART_COLORS = ["#a855f7", "#d946ef", "#f59e0b", "#f43f5e", "#22d3ee", "#10b981"];

export default function AdminDashboard() {
  const { config } = useAuth();
  const [tab, setTab] = useState("overview");
  const [complaintPrefilter, setComplaintPrefilter] = useState(null);
  const [usersPrefilter, setUsersPrefilter] = useState(null);

  function navTo(target, prefilters) {
    if (target === "complaints") setComplaintPrefilter({ ...(prefilters || {}), _ts: Date.now() });
    else if (target === "users") setUsersPrefilter({ ...(prefilters || {}), _ts: Date.now() });
    setTab(target);
  }

  return (
    <AppShell title="Command Center" subtitle="Dispatch, resolve, and analyze broadband operations across Goa." nav={[
      { to: "/admin", label: "Dashboard", testid: "nav-dashboard" },
    ]}>
      <div className="mb-4 flex flex-wrap gap-2">
        <div data-testid="sms-status-pill" className={`inline-flex items-center gap-2 px-3 py-1 rounded-full glass text-xs font-mono-alt uppercase tracking-wider ${config.sms_enabled ? "text-emerald-300" : "text-amber-300"}`}>
          <span className={`w-2 h-2 rounded-full ${config.sms_enabled ? "bg-emerald-400" : "bg-amber-400"}`} />
          SMS · {config.sms_enabled ? "Live (Twilio)" : "Mock (add TWILIO_* keys to .env)"}
        </div>
      </div>
      <OutageBanner role="admin" />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-900 border border-slate-800 mb-6">
          <TabsTrigger data-testid="tab-overview" value="overview">Overview</TabsTrigger>
          <TabsTrigger data-testid="tab-complaints" value="complaints">Complaints</TabsTrigger>
          <TabsTrigger data-testid="tab-network" value="network"><AlertOctagon className="w-3.5 h-3.5 mr-1 text-rose-300" /> Network</TabsTrigger>
          <TabsTrigger data-testid="tab-users" value="users">Users</TabsTrigger>
          <TabsTrigger data-testid="tab-roles" value="roles">Roles</TabsTrigger>
          <TabsTrigger data-testid="tab-groups" value="groups">Groups</TabsTrigger>
          <TabsTrigger data-testid="tab-settings" value="settings">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><Overview onNavigate={navTo} /></TabsContent>
        <TabsContent value="complaints"><ComplaintsPane config={config} prefilter={complaintPrefilter} /></TabsContent>
        <TabsContent value="network"><NetworkPane config={config} /></TabsContent>
        <TabsContent value="users"><UsersPane config={config} prefilter={usersPrefilter} /></TabsContent>
        <TabsContent value="roles"><RolesPane /></TabsContent>
        <TabsContent value="groups"><GroupsPane config={config} /></TabsContent>
        <TabsContent value="settings"><SettingsPane /></TabsContent>
      </Tabs>
    </AppShell>
  );
}

function Overview({ onNavigate }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/analytics/overview"); setData(data); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 5000); return () => clearInterval(id); }, [load]);

  if (!data) return <div className="tactical-card p-10 text-center text-slate-400">Loading analytics…</div>;

  const locData = Object.entries(data.by_location).map(([name, value]) => ({ name, value }));
  const statusData = Object.entries(data.by_status).map(([name, value]) => ({ name, value }));
  const activeCount = (data.by_status["Open"] || 0) + (data.by_status["Assigned"] || 0) + (data.by_status["In Progress"] || 0);
  const overdueCount = data.overdue_count || 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Kpi testid="kpi-total" icon={<AlertTriangle className="w-5 h-5" />} label="Total tickets" value={data.total} tint="blue" onClick={() => onNavigate("complaints", { status: "all" })} />
        <Kpi testid="kpi-active" icon={<Wrench className="w-5 h-5" />} label="Active" value={activeCount} tint="amber" onClick={() => onNavigate("complaints", { status: "active" })} />
        <Kpi testid="kpi-overdue" icon={<AlertOctagon className="w-5 h-5" />} label={`Overdue (>${data.overdue_hours ?? 4}h)`} value={overdueCount} tint="rose" pulse={overdueCount > 0} onClick={() => onNavigate("complaints", { status: "overdue" })} />
        <Kpi testid="kpi-resolved" icon={<ShieldCheck className="w-5 h-5" />} label="Resolved" value={data.by_status["Resolved"] || 0} tint="emerald" onClick={() => onNavigate("complaints", { status: "Resolved" })} />
        <Kpi testid="kpi-techs" icon={<Users className="w-5 h-5" />} label="Technicians" value={data.tech_perf.length} tint="cyan" onClick={() => onNavigate("complaints", { status: "Assigned" })} />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="tactical-card p-5">
          <div className="flex items-center justify-between mb-3"><h3 className="font-display font-bold text-white">Complaints by Location</h3></div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={locData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={12} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, color: "#e2e8f0" }} />
              <Bar dataKey="value" fill="#a855f7" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="tactical-card p-5">
          <h3 className="font-display font-bold text-white mb-3">Status Distribution</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={2}>
                {statusData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, color: "#e2e8f0" }} />
              <Legend wrapperStyle={{ color: "#94a3b8", fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="tactical-card p-5 lg:col-span-2">
          <h3 className="font-display font-bold text-white mb-3">7-day Trend</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.trend}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#a855f7" stopOpacity={0.6} /><stop offset="100%" stopColor="#a855f7" stopOpacity={0} /></linearGradient>
                <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.6} /><stop offset="100%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="date" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, color: "#e2e8f0" }} />
              <Area type="monotone" dataKey="created" stroke="#a855f7" fill="url(#g1)" />
              <Area type="monotone" dataKey="resolved" stroke="#10b981" fill="url(#g2)" />
              <Legend wrapperStyle={{ color: "#94a3b8", fontSize: 12 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="tactical-card p-5">
        <h3 className="font-display font-bold text-white mb-3">Technician Performance</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 font-mono-alt text-xs uppercase tracking-wider border-b border-slate-800">
                <th className="py-2 pr-4">Technician</th>
                <th className="py-2 pr-4">Location</th>
                <th className="py-2 pr-4">Assigned</th>
                <th className="py-2 pr-4">Resolved</th>
                <th className="py-2 pr-4">Avg Resolution (hrs)</th>
              </tr>
            </thead>
            <tbody>
              {data.tech_perf.length === 0 ? (
                <tr><td colSpan={5} className="py-6 text-center text-slate-500">No technicians yet.</td></tr>
              ) : data.tech_perf.map((t) => (
                <tr key={t.id} className="border-b border-slate-900">
                  <td className="py-2 pr-4 text-slate-200">{t.name}</td>
                  <td className="py-2 pr-4 text-slate-400">{t.location || "—"}</td>
                  <td className="py-2 pr-4 text-slate-200">{t.assigned}</td>
                  <td className="py-2 pr-4 text-emerald-300">{t.resolved}</td>
                  <td className="py-2 pr-4 text-slate-300 font-mono-alt">{t.avg_hours ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ComplaintsPane({ config, prefilter }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [locFilter, setLocFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [openNew, setOpenNew] = useState(false);
  const [activeGroup, setActiveGroup] = useState(false); // when true, filter to Open/Assigned/In Progress
  const [overdueGroup, setOverdueGroup] = useState(false); // when true, filter to Open older than overdue_hours

  useEffect(() => {
    if (!prefilter) return;
    if (prefilter.status === "all") {
      setActiveGroup(false); setOverdueGroup(false); setStatusFilter("all"); setLocFilter("all"); setPriorityFilter("all"); setQ("");
    } else if (prefilter.status === "active") {
      setActiveGroup(true); setOverdueGroup(false); setStatusFilter("all"); setLocFilter("all"); setPriorityFilter("all"); setQ("");
    } else if (prefilter.status === "overdue") {
      setOverdueGroup(true); setActiveGroup(false); setStatusFilter("Open"); setLocFilter("all"); setPriorityFilter("all"); setQ("");
    } else if (prefilter.status) {
      setActiveGroup(false); setOverdueGroup(false); setStatusFilter(prefilter.status); setLocFilter("all"); setPriorityFilter("all"); setQ("");
    }
    if (prefilter.location) setLocFilter(prefilter.location);
    if (prefilter.priority) setPriorityFilter(prefilter.priority);
  }, [prefilter]);

  const load = useCallback(async () => {
    try {
      const params = {
        q: q || undefined,
        location: locFilter !== "all" ? locFilter : undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        priority: priorityFilter !== "all" ? priorityFilter : undefined,
      };
      const { data } = await api.get("/complaints", { params });
      setItems(data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }, [q, locFilter, statusFilter, priorityFilter]);

  useEffect(() => { load(); const id = setInterval(load, 5000); return () => clearInterval(id); }, [load]);

  const ACTIVE = new Set(["Open", "Assigned", "In Progress"]);
  const overdueMs = (config?.overdue_hours ?? 4) * 3600 * 1000;
  let visibleItems = activeGroup ? items.filter((i) => ACTIVE.has(i.status)) : items;
  if (overdueGroup) {
    visibleItems = visibleItems.filter((i) => i.status === "Open" && (Date.now() - new Date(i.created_at).getTime()) > overdueMs);
  }

  function exportCsv() {
    const token = localStorage.getItem("np_token") || "";
    const params = new URLSearchParams({ auth: token });
    if (q) params.set("q", q);
    if (locFilter !== "all") params.set("location", locFilter);
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (priorityFilter !== "all") params.set("priority", priorityFilter);
    window.location.href = `${API}/complaints/export.csv?${params.toString()}`;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between gap-2 items-center">
        {overdueGroup ? (
          <div data-testid="overdue-filter-pill" className="pulse-danger inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-500/20 text-rose-100 border border-rose-500/60 text-xs font-mono-alt uppercase tracking-wider">
            <AlertOctagon className="w-3 h-3" /> Overdue only · Open &gt; {config?.overdue_hours ?? 4}h
            <button onClick={() => setOverdueGroup(false)} className="ml-1 text-rose-200 hover:text-white" aria-label="Clear filter">✕</button>
          </div>
        ) : activeGroup ? (
          <div data-testid="active-filter-pill" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/15 text-amber-200 border border-amber-500/40 text-xs font-mono-alt uppercase tracking-wider">
            <Wrench className="w-3 h-3" /> Active only · Open + Assigned + In Progress
            <button onClick={() => setActiveGroup(false)} className="ml-1 text-amber-300 hover:text-white" aria-label="Clear filter">✕</button>
          </div>
        ) : <div />}
        <div className="flex flex-wrap gap-2">
          <Button data-testid="export-csv-btn" onClick={exportCsv} variant="outline" className="border-purple-500/40 bg-purple-500/10 hover:bg-purple-500/20 text-purple-200">
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
          <Button data-testid="raise-complaint-btn" onClick={() => setOpenNew(true)} className="bg-purple-500 hover:bg-purple-600">
            <Plus className="w-4 h-4 mr-1.5" /> Raise complaint
          </Button>
        </div>
      </div>
      <div className="tactical-card p-4 grid gap-3 md:grid-cols-5">
        <div className="relative md:col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ticket, customer, address…" className="bg-slate-900 border-slate-700 pl-9" />
        </div>
        <Select value={locFilter} onValueChange={setLocFilter}>
          <SelectTrigger data-testid="admin-location-filter" className="bg-slate-900 border-slate-700"><SelectValue placeholder="Location" /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
            <SelectItem value="all">All locations</SelectItem>
            {config.locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="admin-status-filter" className="bg-slate-900 border-slate-700"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
            <SelectItem value="all">All statuses</SelectItem>
            {config.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger data-testid="admin-priority-filter" className="bg-slate-900 border-slate-700"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
            <SelectItem value="all">All priorities</SelectItem>
            {config.priorities.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-3">
        {visibleItems.length === 0 ? (
          <div className="tactical-card p-10 text-center text-slate-400">No complaints match your filters.</div>
        ) : visibleItems.map((c) => (
          <button key={c.id} data-testid={`admin-row-${c.ticket_no}`} onClick={() => setSelected(c.id)} className={`tactical-card p-4 text-left hover:border-purple-500/40 ${statusStripClass(c.status)}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono-alt text-xs text-purple-300 tracking-widest">{c.ticket_no}</span>
                  <StatusBadge status={c.status} />
                  <PriorityBadge priority={c.priority} />
                  <OverdueBadge complaint={c} hours={config?.overdue_hours ?? 4} />
                  <OutageBadge complaint={c} />
                </div>
                <div className="font-display font-semibold text-white mt-1.5">{c.customer_name} · <span className="text-slate-400 font-normal">{c.location}</span></div>
                <div className="text-sm text-slate-400 line-clamp-2">{c.description}</div>
                <div className="text-xs text-slate-500 mt-2 font-mono-alt">
                  {new Date(c.created_at).toLocaleString()} · {c.assigned_to_name ? <span className="text-blue-300">Tech: {c.assigned_to_name}</span> : <span className="text-amber-300">Unassigned</span>}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      <ComplaintDetailDialog complaintId={selected} open={!!selected} onOpenChange={(v) => !v && setSelected(null)} onChanged={load} />
      <RaiseComplaintDialog open={openNew} onOpenChange={setOpenNew} onCreated={load} />
    </div>
  );
}

function UsersPane({ config, prefilter }) {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [roleF, setRoleF] = useState("all");
  const [openNew, setOpenNew] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "technician", phone: "", location: "", group_id: "" });

  useEffect(() => {
    if (prefilter?.role) setRoleF(prefilter.role);
  }, [prefilter]);

  async function load() {
    try {
      const params = roleF !== "all" ? { role: roleF } : {};
      const { data } = await api.get("/users", { params });
      setUsers(data);
      const g = await api.get("/groups");
      setGroups(g.data);
      const r = await api.get("/roles");
      setRoles(r.data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }
  useEffect(() => { load(); }, [roleF]); // eslint-disable-line react-hooks/exhaustive-deps

  function copyCreds(username, password) {
    const line = `Username: ${username}\nPassword: ${password}`;
    navigator.clipboard?.writeText(line).then(
      () => toast.success("Credentials copied to clipboard"),
      () => toast.info(line)
    );
  }

  async function submit(e) {
    e.preventDefault();
    try {
      const username = form.email;
      const password = form.password;
      await api.post("/users", { ...form, group_id: form.group_id || null, location: form.location || null });
      toast.success(`Created ${form.name}`, {
        description: `Username: ${username} · Password: ${password}`,
        duration: 15000,
        action: { label: "Copy", onClick: () => copyCreds(username, password) },
      });
      setOpenNew(false);
      setForm({ name: "", email: "", password: "", role: "technician", phone: "", location: "", group_id: "" });
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }

  async function submitReset(e) {
    e.preventDefault();
    if (!resetTarget || resetPw.length < 6) return;
    try {
      await api.post(`/users/${resetTarget.id}/reset-password`, { password: resetPw });
      const username = resetTarget.email;
      const password = resetPw;
      toast.success(`Password reset for ${resetTarget.name}`, {
        description: `Username: ${username} · Password: ${password}`,
        duration: 15000,
        action: { label: "Copy", onClick: () => copyCreds(username, password) },
      });
      setResetTarget(null); setResetPw("");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between gap-3">
        <Select value={roleF} onValueChange={setRoleF}>
          <SelectTrigger data-testid="users-role-filter" className="bg-slate-900 border-slate-700 w-[220px]"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
            <SelectItem value="all">All roles</SelectItem>
            {config.roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <Button data-testid="new-user-btn" className="bg-purple-500 hover:bg-purple-600"><Plus className="w-4 h-4 mr-1" /> New user</Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 max-w-lg">
            <DialogHeader><DialogTitle>Create user</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Name</Label><Input data-testid="user-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-slate-900 border-slate-700 mt-1" /></div>
              <div className="col-span-2"><Label>Username</Label><Input data-testid="user-email" type="text" required minLength={3} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="e.g. rahul (used for sign-in)" className="bg-slate-900 border-slate-700 mt-1" /></div>
              <div className="col-span-2"><Label>Password</Label><Input data-testid="user-password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-slate-900 border-slate-700 mt-1" /></div>
              <div><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role" className="bg-slate-900 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                    {roles.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}{r.is_system ? "" : ` · ${r.tier} tier`}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-slate-900 border-slate-700 mt-1" /></div>
              <div><Label>Location</Label>
                <Select value={form.location || "all"} onValueChange={(v) => setForm({ ...form, location: v === "all" ? "" : v })}>
                  <SelectTrigger className="bg-slate-900 border-slate-700 mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                    <SelectItem value="all">All locations</SelectItem>
                    {config.locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Group</Label>
                <Select value={form.group_id} onValueChange={(v) => setForm({ ...form, group_id: v })}>
                  <SelectTrigger className="bg-slate-900 border-slate-700 mt-1"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                    {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="user-submit" type="submit" className="col-span-2 bg-purple-500 hover:bg-purple-600">Create</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="tactical-card p-3 mb-2 text-xs text-slate-400 flex items-start gap-2 border-l-2 border-purple-500/50">
        <KeyRound className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
        <span>Passwords are one-way encrypted (bcrypt) and can never be read back. Use <b className="text-purple-300">Reset password</b> to set a new one — the fresh credential is shown once so you can copy and share.</span>
      </div>
      <div className="tactical-card p-2 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-400 font-mono-alt text-xs uppercase tracking-wider border-b border-slate-800">
              <th className="py-2 px-3">Name</th>
              <th className="py-2 px-3">Username</th>
              <th className="py-2 px-3">Password</th>
              <th className="py-2 px-3">Role</th>
              <th className="py-2 px-3">Location</th>
              <th className="py-2 px-3">Phone</th>
              <th className="py-2 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-900 hover:bg-slate-900/40">
                <td className="py-2 px-3 text-slate-200">{u.name}</td>
                <td className="py-2 px-3 text-slate-300 font-mono-alt">{u.email}</td>
                <td className="py-2 px-3">
                  <span className="font-mono-alt text-slate-500 tracking-widest">••••••••</span>
                  <span className="ml-2 text-[10px] uppercase text-slate-600 font-mono-alt">encrypted</span>
                </td>
                <td className="py-2 px-3"><span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/30" title={u.role !== (u.role_label || u.role) ? `${u.role} tier` : ""}>{u.role_label || u.role}</span></td>
                <td className="py-2 px-3 text-slate-400">{u.location || "All locations"}</td>
                <td className="py-2 px-3 text-slate-400">{u.phone || "—"}</td>
                <td className="py-2 px-3 text-right whitespace-nowrap">
                  {u.role !== "admin" && (
                    <>
                      <Button data-testid={`reset-user-${u.email}`} onClick={() => { setResetTarget(u); setResetPw(""); }} variant="ghost" size="sm" className="text-purple-300 hover:bg-purple-500/10 mr-1" title="Reset password"><KeyRound className="w-4 h-4" /></Button>
                      <Button data-testid={`delete-user-${u.email}`} onClick={() => remove(u.id)} variant="ghost" size="sm" className="text-rose-300 hover:bg-rose-500/10" title="Delete user"><Trash2 className="w-4 h-4" /></Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!resetTarget} onOpenChange={(v) => !v && setResetTarget(null)}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 max-w-md">
          <DialogHeader><DialogTitle>Reset password · {resetTarget?.name}</DialogTitle></DialogHeader>
          <form onSubmit={submitReset} className="space-y-3">
            <div className="text-xs text-slate-400">Username: <span className="font-mono-alt text-purple-300">{resetTarget?.email}</span></div>
            <div>
              <Label>New password</Label>
              <Input data-testid="reset-password-input" required minLength={6} type="text" value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="Min 6 characters" className="bg-slate-900 border-slate-700 mt-1 font-mono-alt" />
              <div className="text-[11px] text-slate-500 mt-1">This will be shown once and can be copied.</div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button data-testid="reset-submit" type="submit" className="flex-1 bg-purple-500 hover:bg-purple-600"><KeyRound className="w-4 h-4 mr-1.5" /> Reset & copy</Button>
              <Button type="button" onClick={() => setResetTarget(null)} variant="outline" className="border-slate-700 hover:bg-slate-800">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GroupsPane({ config }) {
  const [groups, setGroups] = useState([]);
  const [form, setForm] = useState({ name: "", location: "", description: "" });

  async function load() {
    try { const { data } = await api.get("/groups"); setGroups(data); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }
  useEffect(() => { load(); }, []);

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      await api.post("/groups", { ...form, location: form.location || null });
      toast.success("Group created");
      setForm({ name: "", location: "", description: "" });
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this group?")) return;
    try { await api.delete(`/groups/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <form onSubmit={submit} className="tactical-card p-5 space-y-3 lg:col-span-1">
        <h3 className="font-display font-bold text-white">Create Group</h3>
        <div><Label>Name</Label><Input data-testid="group-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Margao Rapid Response" className="bg-slate-900 border-slate-700 mt-1" /></div>
        <div><Label>Location</Label>
          <Select value={form.location || "all"} onValueChange={(v) => setForm({ ...form, location: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="group-location" className="bg-slate-900 border-slate-700 mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
              <SelectItem value="all">All locations</SelectItem>
              {config.locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-[11px] text-slate-500 mt-1">Choose "All locations" for teams that cover every area.</div>
        </div>
        <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-slate-900 border-slate-700 mt-1" /></div>
        <Button data-testid="group-submit" type="submit" className="w-full bg-purple-500 hover:bg-purple-600"><Plus className="w-4 h-4 mr-1" /> Create</Button>
      </form>
      <div className="lg:col-span-2 space-y-3">
        {groups.length === 0 ? (
          <div className="tactical-card p-10 text-center text-slate-400">No groups yet — create your first technician group.</div>
        ) : groups.map((g) => (
          <div key={g.id} className="tactical-card p-4 flex items-center justify-between">
            <div>
              <div className="font-display font-semibold text-white">{g.name}</div>
              <div className="text-xs text-slate-400 mt-0.5">{g.location || "All locations"} · {g.description || "—"}</div>
            </div>
            <Button data-testid={`delete-group-${g.name}`} onClick={() => remove(g.id)} variant="ghost" size="icon" className="text-rose-300 hover:bg-rose-500/10"><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function RolesPane() {
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState({ name: "", tier: "technician", description: "" });

  async function load() {
    try { const { data } = await api.get("/roles"); setRoles(data); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }
  useEffect(() => { load(); }, []);

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      await api.post("/roles", form);
      toast.success("Role created");
      setForm({ name: "", tier: "technician", description: "" });
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this role?")) return;
    try { await api.delete(`/roles/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <form onSubmit={submit} className="tactical-card p-5 space-y-3 lg:col-span-1">
        <h3 className="font-display font-bold text-white">Create Role</h3>
        <p className="text-xs text-slate-500 -mt-1">Pick a permission tier — <span className="text-purple-300">admin</span> unlocks the full console, <span className="text-purple-300">technician</span> is field-work only.</p>
        <div>
          <Label>Role name</Label>
          <Input data-testid="role-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Senior Technician, Call Center" className="bg-slate-900 border-slate-700 mt-1" />
        </div>
        <div>
          <Label>Permission tier</Label>
          <Select value={form.tier} onValueChange={(v) => setForm({ ...form, tier: v })}>
            <SelectTrigger data-testid="role-tier" className="bg-slate-900 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
              <SelectItem value="admin">admin · full access</SelectItem>
              <SelectItem value="technician">technician · field ops only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Description</Label>
          <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional" className="bg-slate-900 border-slate-700 mt-1" />
        </div>
        <Button data-testid="role-submit" type="submit" className="w-full bg-purple-500 hover:bg-purple-600"><Plus className="w-4 h-4 mr-1" /> Create role</Button>
      </form>
      <div className="lg:col-span-2 space-y-3">
        {roles.length === 0 ? (
          <div className="tactical-card p-10 text-center text-slate-400">No roles yet.</div>
        ) : roles.map((r) => (
          <div key={r.id} data-testid={`role-row-${r.name}`} className="tactical-card p-4 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <div className="font-display font-semibold text-white">{r.name}</div>
                {r.is_system && <span className="text-[10px] font-mono-alt uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700">built-in</span>}
                <span className="text-[10px] font-mono-alt uppercase tracking-wider px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-500/30">{r.tier} tier</span>
              </div>
              <div className="text-xs text-slate-400 mt-0.5">{r.description || "—"}</div>
            </div>
            {!r.is_system && (
              <Button data-testid={`delete-role-${r.name}`} onClick={() => remove(r.id)} variant="ghost" size="icon" className="text-rose-300 hover:bg-rose-500/10"><Trash2 className="w-4 h-4" /></Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsPane() {
  const { config, refresh } = useAuth();
  const [hours, setHours] = useState(config?.overdue_hours ?? 4);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setHours(config?.overdue_hours ?? 4); }, [config]);

  async function save() {
    setSaving(true);
    try {
      await api.patch("/settings", { overdue_hours: Number(hours) });
      toast.success(`Overdue threshold set to ${hours} hours`);
      await refresh();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="tactical-card p-6 space-y-4">
        <div>
          <h3 className="font-display font-bold text-white text-lg">Overdue threshold</h3>
          <p className="text-sm text-slate-400 mt-1">A ticket that stays <span className="text-rose-300 font-semibold">Open</span> longer than this window is flagged with the pulsing red <span className="pulse-danger inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-500/20 text-rose-200 border border-rose-500/60 text-[10px] font-mono-alt uppercase tracking-wider">Overdue</span> badge.</p>
        </div>
        <div>
          <Label>Flag ticket as overdue after</Label>
          <Select value={String(hours)} onValueChange={(v) => setHours(v)}>
            <SelectTrigger data-testid="overdue-hours-select" className="bg-slate-900 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
              <SelectItem value="1">1 hour</SelectItem>
              <SelectItem value="2">2 hours</SelectItem>
              <SelectItem value="4">4 hours (default)</SelectItem>
              <SelectItem value="6">6 hours</SelectItem>
              <SelectItem value="8">8 hours</SelectItem>
              <SelectItem value="12">12 hours</SelectItem>
              <SelectItem value="24">24 hours</SelectItem>
              <SelectItem value="48">48 hours</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="save-settings-btn" onClick={save} disabled={saving || Number(hours) === (config?.overdue_hours ?? 4)} className="bg-purple-500 hover:bg-purple-600">
          {saving ? "Saving…" : "Save threshold"}
        </Button>
      </div>
      <div className="tactical-card p-6 space-y-3 text-sm text-slate-400">
        <h4 className="font-display font-semibold text-white">Live integration status</h4>
        <StatusLine label="Push notifications" value={config?.push_enabled ? "Live (VAPID)" : "Off"} good={!!config?.push_enabled} />
        <StatusLine label="SMS (Twilio)" value={config?.sms_enabled ? "Live" : "Mock — add TWILIO_* env keys"} good={!!config?.sms_enabled} />
        <StatusLine label="Photo uploads" value={config?.uploads_enabled ? "Live (Object Storage)" : "Off"} good={!!config?.uploads_enabled} />
      </div>
    </div>
  );
}

function NetworkPane({ config }) {
  const [devices, setDevices] = useState([]);
  const [outages, setOutages] = useState([]);
  const [form, setForm] = useState({ names: "", device_type: "OLT", location: "", notes: "" });
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertForm, setAlertForm] = useState({ device_id: "", reason: "", sms_customers: true });
  const [saving, setSaving] = useState(false);

  const activeOutages = outages.filter((o) => o.status === "active");
  const historyOutages = outages.filter((o) => o.status !== "active").slice(0, 15);
  const devicesById = Object.fromEntries(devices.map((d) => [d.id, d]));
  const activeDeviceIds = new Set(activeOutages.map((o) => o.device_id));

  const load = useCallback(async () => {
    try {
      const d = await api.get("/devices");
      const o = await api.get("/outages");
      setDevices(d.data);
      setOutages(o.data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }, []);

  useEffect(() => { load(); const id = setInterval(load, 5000); return () => clearInterval(id); }, [load]);

  async function createDevice(e) {
    e.preventDefault();
    if (!form.names.trim() || !form.location) { toast.error("Enter device name(s) & pick location"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/devices/bulk", {
        names: form.names,
        device_type: form.device_type,
        location: form.location,
        notes: form.notes || null,
      });
      const add = data.added_count || 0;
      const skip = data.skipped_count || 0;
      if (add) toast.success(`Added ${add} ${form.device_type}${add === 1 ? "" : "s"}${skip ? ` · skipped ${skip} duplicate${skip === 1 ? "" : "s"}` : ""}`);
      else toast.error(`Nothing added — ${skip} name(s) already exist`);
      setForm({ names: "", device_type: form.device_type, location: "", notes: "" });
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  async function removeDevice(d) {
    if (!window.confirm(`Delete device ${d.name}?`)) return;
    try { await api.delete(`/devices/${d.id}`); toast.success("Device deleted"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }

  async function submitAlert(e) {
    e.preventDefault();
    if (!alertForm.device_id || !alertForm.reason.trim()) { toast.error("Pick a device and enter a reason"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/outages", alertForm);
      toast.error(`🚨 HIGH ALERT · ${data.device_name} DOWN — ${data.linked_count} ticket(s) linked${data.sms_blast_count ? ` · SMS sent to ${data.sms_blast_count}` : ""}`, { duration: 8000 });
      setAlertOpen(false);
      setAlertForm({ device_id: "", reason: "", sms_customers: true });
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  return (
    <div className="space-y-6">
      <div className="tactical-card p-5 border-rose-500/40 bg-gradient-to-br from-rose-950/40 to-slate-900/60 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-start gap-3 max-w-xl">
          <div className="w-11 h-11 rounded-lg bg-rose-500/20 border border-rose-500/50 flex items-center justify-center text-rose-300 shrink-0">
            <AlertOctagon className="w-6 h-6" />
          </div>
          <div>
            <div className="font-display font-extrabold text-white text-lg">Major Outage · HIGH ALERT</div>
            <div className="text-sm text-slate-400 mt-1">One OLT/Switch down means dozens of complaints. Fire a HIGH ALERT to auto-tag every ticket in that area, ping all technicians, and show a pulsing banner across every screen.</div>
          </div>
        </div>
        <Button
          data-testid="high-alert-btn"
          onClick={() => { if (!devices.length) { toast.error("Add a device first"); return; } setAlertOpen(true); }}
          className="bg-rose-600 hover:bg-rose-500 text-white shadow-lg shadow-rose-500/40 h-12 px-5 text-base font-bold"
        >
          <AlertOctagon className="w-5 h-5 mr-2" /> HIGH ALERT · Device down
        </Button>
      </div>

      {activeOutages.length > 0 && (
        <div className="tactical-card p-5">
          <h3 className="font-display font-bold text-white mb-3 flex items-center gap-2"><Zap className="w-4 h-4 text-rose-400" /> Active outages ({activeOutages.length})</h3>
          <div className="grid gap-3">
            {activeOutages.map((o) => (
              <div key={o.id} data-testid={`active-outage-${o.device_name}`} className="border border-rose-500/40 bg-rose-500/10 rounded-lg p-3 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[240px]">
                  <div className="font-mono-alt text-[10px] uppercase tracking-widest text-rose-300">{o.device_type} · {o.location}</div>
                  <div className="font-display font-bold text-white">{o.device_name}</div>
                  <div className="text-sm text-slate-300">{o.reason}</div>
                  <div className="text-xs text-slate-500 mt-1 font-mono-alt">
                    started {new Date(o.started_at).toLocaleString()} by {o.started_by_name} · {o.linked_count} ticket(s)
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        <form onSubmit={createDevice} className="tactical-card p-5 space-y-3 lg:col-span-1">
          <h3 className="font-display font-bold text-white flex items-center gap-2"><Router className="w-4 h-4 text-purple-300" /> Add Devices</h3>
          <div>
            <Label>Device name(s)</Label>
            <Textarea
              data-testid="device-name"
              required
              rows={5}
              value={form.names}
              onChange={(e) => setForm({ ...form, names: e.target.value })}
              placeholder={"Paste one name per line, e.g.\nOLT-Margao-01\nOLT-Margao-02\nSW-Margao-Core"}
              className="bg-slate-900 border-slate-700 mt-1 font-mono-alt text-sm"
            />
            <div className="text-[11px] text-slate-500 mt-1">One per line (or comma-separated). Duplicates are skipped automatically.</div>
          </div>
          <div>
            <Label>Type (applies to all)</Label>
            <Select value={form.device_type} onValueChange={(v) => setForm({ ...form, device_type: v })}>
              <SelectTrigger data-testid="device-type" className="bg-slate-900 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                {(config.device_types || ["OLT", "Switch", "Other"]).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Location (applies to all)</Label>
            <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })}>
              <SelectTrigger data-testid="device-location" className="bg-slate-900 border-slate-700 mt-1"><SelectValue placeholder="Pick area" /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                {config.locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Input data-testid="device-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Feeder OLTs for Fatorda cluster" className="bg-slate-900 border-slate-700 mt-1" />
          </div>
          <Button data-testid="add-device-btn" type="submit" disabled={saving} className="w-full bg-purple-500 hover:bg-purple-600"><Plus className="w-4 h-4 mr-1" /> {saving ? "Adding…" : "Add devices"}</Button>
        </form>

        <div className="lg:col-span-2 space-y-3">
          <h3 className="font-display font-bold text-white flex items-center gap-2"><Router className="w-4 h-4 text-purple-300" /> Devices ({devices.length})</h3>
          {devices.length === 0 ? (
            <div className="tactical-card p-10 text-center text-slate-400">No devices yet. Register your OLTs & Switches to enable HIGH ALERT.</div>
          ) : devices.map((d) => (
            <div key={d.id} data-testid={`device-row-${d.name}`} className={`tactical-card p-4 flex items-center justify-between gap-3 ${activeDeviceIds.has(d.id) ? "border-rose-500/60 bg-rose-500/5" : ""}`}>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-mono-alt uppercase tracking-widest px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-500/30">{d.device_type}</span>
                  <span className="font-display font-semibold text-white">{d.name}</span>
                  <span className="text-[10px] font-mono-alt uppercase tracking-widest px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700">{d.location}</span>
                  {activeDeviceIds.has(d.id) && (
                    <span className="pulse-danger text-[10px] font-mono-alt uppercase tracking-widest px-1.5 py-0.5 rounded bg-rose-500/25 text-rose-100 border border-rose-500/70">DOWN</span>
                  )}
                </div>
                {d.notes && <div className="text-xs text-slate-400 mt-1">{d.notes}</div>}
              </div>
              <Button data-testid={`delete-device-${d.name}`} onClick={() => removeDevice(d)} variant="ghost" size="icon" className="text-rose-300 hover:bg-rose-500/10"><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
        </div>
      </div>

      {historyOutages.length > 0 && (
        <div className="tactical-card p-5">
          <h3 className="font-display font-bold text-white mb-3">Outage history</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 font-mono-alt text-xs uppercase tracking-wider border-b border-slate-800">
                  <th className="py-2 pr-4">Device</th>
                  <th className="py-2 pr-4">Location</th>
                  <th className="py-2 pr-4">Reason</th>
                  <th className="py-2 pr-4">Started</th>
                  <th className="py-2 pr-4">Resolved</th>
                  <th className="py-2 pr-4">Duration</th>
                  <th className="py-2 pr-4">Tickets</th>
                </tr>
              </thead>
              <tbody>
                {historyOutages.map((o) => {
                  const start = new Date(o.started_at);
                  const end = o.resolved_at ? new Date(o.resolved_at) : null;
                  const durMin = end ? Math.round((end - start) / 60000) : null;
                  const durTxt = durMin == null ? "—" : durMin < 60 ? `${durMin}m` : `${Math.floor(durMin / 60)}h ${durMin % 60}m`;
                  return (
                    <tr key={o.id} className="border-b border-slate-900">
                      <td className="py-2 pr-4"><span className="font-mono-alt text-slate-200">{o.device_name}</span> <span className="text-[10px] text-slate-500 uppercase">{o.device_type}</span></td>
                      <td className="py-2 pr-4 text-slate-400">{o.location}</td>
                      <td className="py-2 pr-4 text-slate-300">{o.reason}</td>
                      <td className="py-2 pr-4 text-slate-400 font-mono-alt text-xs">{start.toLocaleString()}</td>
                      <td className="py-2 pr-4 text-slate-400 font-mono-alt text-xs">{end ? end.toLocaleString() : "—"}</td>
                      <td className="py-2 pr-4 text-emerald-300 font-mono-alt">{durTxt}</td>
                      <td className="py-2 pr-4 text-slate-300">{o.linked_count}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={alertOpen} onOpenChange={setAlertOpen}>
        <DialogContent className="bg-slate-950 border-rose-500/60 text-slate-100 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-200">
              <AlertOctagon className="w-5 h-5 text-rose-400" /> Fire HIGH ALERT
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitAlert} className="space-y-3">
            <div className="text-xs text-slate-400">
              This will flag the device as <b className="text-rose-300">DOWN</b>, auto-link all open tickets in that area, push-notify every technician, and pulse a red banner across the app until resolved.
            </div>
            <div>
              <Label>Which device is down?</Label>
              <Select value={alertForm.device_id} onValueChange={(v) => setAlertForm({ ...alertForm, device_id: v })}>
                <SelectTrigger data-testid="alert-device-select" className="bg-slate-900 border-slate-700 mt-1"><SelectValue placeholder="Pick device" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  {devices.filter((d) => !activeDeviceIds.has(d.id)).map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.device_type} · {d.name} · {d.location}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {activeDeviceIds.size > 0 && <div className="text-[11px] text-slate-500 mt-1">Devices already down are hidden.</div>}
            </div>
            <div>
              <Label>Reason</Label>
              <Input data-testid="alert-reason" required value={alertForm.reason} onChange={(e) => setAlertForm({ ...alertForm, reason: e.target.value })} placeholder="e.g. Fiber cut · Power fail · UPS blown" className="bg-slate-900 border-slate-700 mt-1" />
            </div>
            {alertForm.device_id && devicesById[alertForm.device_id] && (
              <div className="text-xs bg-rose-500/10 border border-rose-500/40 rounded-lg px-3 py-2 text-rose-200">
                Every OPEN complaint in <b>{devicesById[alertForm.device_id].location}</b> will be tagged with this outage automatically.
              </div>
            )}
            <label className="flex items-start gap-2 text-sm text-slate-300 cursor-pointer border border-slate-800 rounded-lg px-3 py-2 hover:bg-slate-900/60">
              <input
                data-testid="alert-sms-toggle"
                type="checkbox"
                checked={alertForm.sms_customers}
                onChange={(e) => setAlertForm({ ...alertForm, sms_customers: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-rose-500"
              />
              <span>
                SMS every affected customer — <span className="text-slate-400">"We detected a {alertForm.device_id && devicesById[alertForm.device_id] ? devicesById[alertForm.device_id].device_type : "OLT"} outage in your area, our team is on it."</span>
                <div className="text-xs text-slate-500 mt-0.5">
                  {config?.sms_enabled ? "Twilio live — SMS will be sent to every linked customer." : "SMS is currently in MOCK mode (add TWILIO_* keys to .env). Message will be logged for now."}
                </div>
              </span>
            </label>
            <div className="flex gap-2 pt-1">
              <Button data-testid="confirm-high-alert" type="submit" disabled={saving} className="flex-1 bg-rose-600 hover:bg-rose-500 text-white font-bold">
                {saving ? "Broadcasting…" : "Fire HIGH ALERT"}
              </Button>
              <Button type="button" onClick={() => setAlertOpen(false)} variant="outline" className="border-slate-700 hover:bg-slate-800">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function StatusLine({ label, value, good }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-800 pb-2 last:border-0 last:pb-0">
      <span className="text-slate-300">{label}</span>
      <span className={`text-xs font-mono-alt uppercase tracking-wider px-2 py-0.5 rounded-full border ${good ? "text-emerald-300 bg-emerald-500/10 border-emerald-500/30" : "text-amber-300 bg-amber-500/10 border-amber-500/30"}`}>{value}</span>
    </div>
  );
}

function Kpi({ icon, label, value, tint, onClick, testid, pulse = false }) {
  const tints = { blue: "text-purple-300 bg-purple-500/10 border-purple-500/30", amber: "text-amber-300 bg-amber-500/10 border-amber-500/30", emerald: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30", cyan: "text-cyan-300 bg-cyan-500/10 border-cyan-500/30", rose: "text-rose-300 bg-rose-500/15 border-rose-500/50" };
  const Tag = onClick ? "button" : "div";
  return (
    <Tag data-testid={testid} onClick={onClick} className={`tactical-card p-4 flex items-center gap-3 w-full text-left ${pulse ? "pulse-danger border-rose-500/60" : ""} ${onClick ? "hover:border-purple-500/50 hover:bg-slate-900/60 transition cursor-pointer" : ""}`}>
      <div className={`w-10 h-10 rounded-lg border flex items-center justify-center ${tints[tint]}`}>{icon}</div>
      <div>
        <div className="text-xs font-mono-alt uppercase tracking-widest text-slate-500">{label}</div>
        <div className={`font-display font-bold text-2xl ${pulse ? "text-rose-100" : "text-white"}`}>{value}</div>
      </div>
    </Tag>
  );
}
